/* eslint-disable complexity */
/* eslint-disable max-lines */
import {
  BookWidgetAirport,
  TripTypeOptions
} from '@com-delta-idp/mnx-capability-flight-shop-ui';
import { SelectedRange } from '@com-delta-omnichannel/mach/lib/components/organisms/date-picker/models/date-picker.interfaces';
import { createReducer, on } from '@ngrx/store';
import * as fromBookWidgetFieldsUpdaterAdapters from '../../adapters/book-widget-fields-update';
import { maxFlightCountForMc } from '../../constants/common.constants';
import { FlightShopWidgetTemplates } from '../../constants/flight-shop.constants';
import { BookWidgetState } from '../../models/states/book-widget-state.interfaces';
import * as fromBookWidgetActions from '../actions/book-widget.actions';

export const BookWidgetStateKey = 'bookWidget';

const initialState: BookWidgetState = {
  fields: {
    airportsField: {
      airports: [
        { fromAirportcode: '', fromCity: '', toAirportcode: '', toCity: '' }
      ],
      showAddFlightBtn: false,
      showRemoveFlightBtn: false
    },
    tripTypesField: {
      disable: false,
      dropdownOptions: [],
      selectedTripType: TripTypeOptions.ROUND_TRIP
    },
    datesField: {
      dates: [{ start: null, end: null, total: 0 }]
    },
    passengerField: {
      disable: false,
      dropdownOptions: [],
      selectedPaxValue: ''
    },
    shopMilesField: {
      checked: false,
      disable: false,
      hide: false
    },
    refundableFareField: {
      checked: false,
      disable: false,
      hide: false
    },
    flexDatesField: {
      checked: false,
      disable: false,
      hide: false
    },
    flexAirportsField: {
      checked: false,
      disable: false,
      hide: false
    },
    basicFaresField: {
      checked: true,
      disable: false,
      hide: false
    },
    bestFareOptionsField: {
      disable: false,
      dropdownOptions: [],
      hide: false,
      selectedBestFare: ''
    },
    meetingEventCodeField: {
      disable: false,
      hide: false,
      meetingEventCodeValue: '',
      disclaimerMessage: ''
    },
    edocsLink: {
      disable: false,
      hide: false,
      src: ''
    }
  },
  startFieldsValidation: false,
  fieldsErrorCount: 0,
  templateType: FlightShopWidgetTemplates.APP
};

export const bookWidgetReducer = createReducer(
  initialState,
  on(
    fromBookWidgetActions.SetFlightShopTemplateType,
    (state, { templateType }) => ({
      ...state,
      templateType
    })
  ),
  on(
    fromBookWidgetActions.InitBookWidgetFields,
    (state, { bookWidgetFields }) => ({
      ...state,
      fields: { ...bookWidgetFields },
      fieldsErrorCount: 0
    })
  ),
  on(
    fromBookWidgetActions.ApplyBookWidgetFieldChangeRules,
    (state, { bookWidgetFields }) => ({
      ...state,
      fields: { ...bookWidgetFields }
    })
  ),
  on(
    fromBookWidgetActions.BookWidgetFieldChange,
    (state, { bookWidgetFieldUpdateEvent }) => ({
      ...fromBookWidgetFieldsUpdaterAdapters.bookWidgetFieldsUpdate(
        state,
        bookWidgetFieldUpdateEvent
      )
    })
  ),
  on(fromBookWidgetActions.SwapAirports, (state, { flightIndex }) => {
    const updatedAirports: BookWidgetAirport[] = [
      ...state.fields.airportsField.airports.map((airport) => ({ ...airport }))
    ];
    const { fromAirportcode, fromCity, toAirportcode, toCity } =
      updatedAirports[flightIndex];

    updatedAirports[flightIndex].fromAirportcode = toAirportcode;
    updatedAirports[flightIndex].fromCity = toCity;
    updatedAirports[flightIndex].toAirportcode = fromAirportcode;
    updatedAirports[flightIndex].toCity = fromCity;

    return {
      ...state,
      fields: {
        ...state.fields,
        airportsField: {
          ...state.fields.airportsField,
          airports: updatedAirports
        }
      }
    };
  }),
  on(fromBookWidgetActions.AddFlightsForMc, (state) => {
    const updatedAirports: BookWidgetAirport[] = [
      ...state.fields.airportsField.airports
    ];
    const { toAirportcode = '', toCity = '' } =
      updatedAirports[updatedAirports.length - 1];

    updatedAirports.push({
      fromAirportcode: toAirportcode,
      fromCity: toCity,
      toAirportcode: '',
      toCity: '',
      connectionAirport: '',
      connectionCity: ''
    });

    const updatedDates: SelectedRange[] = [...state.fields.datesField.dates];
    updatedDates.push({
      start: updatedDates[updatedDates.length - 1]?.start || null,
      end: null,
      total: 0
    });

    return {
      ...state,
      fields: {
        ...state.fields,
        airportsField: {
          ...state.fields.airportsField,
          showAddFlightBtn: updatedAirports.length < maxFlightCountForMc,
          showRemoveFlightBtn: updatedAirports.length > 2,
          airports: updatedAirports
        },
        datesField: {
          ...state.fields.datesField,
          dates: updatedDates
        }
      }
    };
  }),
  on(fromBookWidgetActions.RemoveFlightForMc, (state) => {
    const updatedAirports: BookWidgetAirport[] = [
      ...state.fields.airportsField.airports
    ];
    updatedAirports.pop();

    const updatedDates: SelectedRange[] = [...state.fields.datesField.dates];
    updatedDates.pop();

    return {
      ...state,
      fields: {
        ...state.fields,
        airportsField: {
          ...state.fields.airportsField,
          showAddFlightBtn: updatedAirports.length < maxFlightCountForMc,
          showRemoveFlightBtn: updatedAirports.length > 2,
          airports: updatedAirports
        },
        datesField: {
          ...state.fields.datesField,
          dates: updatedDates
        }
      }
    };
  }),
  on(
    fromBookWidgetActions.UpdateFieldsFromRecentSearch,
    (state, { bookWidgetFields }) => ({
      ...state,
      fields: { ...bookWidgetFields }
    })
  ),
  on(fromBookWidgetActions.FindFlightInitiated, (state) => ({
    ...state,
    startFieldsValidation: true
  })),
  on(
    fromBookWidgetActions.SetFieldsAfterValidation,
    (state, { bookWidgetFields, fieldsErrorCount }) => {
      return {
        ...state,
        fieldsErrorCount,
        fields: { ...bookWidgetFields }
      };
    }
  ),
  on(
    fromBookWidgetActions.UpdateFieldsFromSavedSearch,
    (state, { bookWidgetFields }) => ({
      ...state,
      fields: { ...bookWidgetFields }
    })
  ),
  on(
    fromBookWidgetActions.UpdateBookWidgetFieldsFromFlex,
    (state, { dates, destinationAirportCode, originAirportCode }) => {
      const updatedAirports: BookWidgetAirport[] = [
        ...state.fields.airportsField.airports
      ];

      const updatedDates: SelectedRange[] = [...state.fields.datesField.dates];

      if (originAirportCode) {
        updatedAirports[0] = {
          ...updatedAirports[0],
          fromAirportcode: originAirportCode,
          fromCity: '',
          fromCountry: '',
          fromCountryCd: ''
        };
      }

      if (destinationAirportCode) {
        updatedAirports[0] = {
          ...updatedAirports[0],
          toAirportcode: destinationAirportCode,
          toCity: '',
          toCountryCd: '',
          toStateCode: ''
        };
      }

      if (dates) {
        updatedDates[0] = {
          ...updatedDates[0],
          start: new Date(dates.departureDate),
          end: dates.returnDate ? new Date(dates.returnDate) : null
        };
      }

      return {
        ...state,
        fields: {
          ...state.fields,
          airportsField: {
            ...state.fields.airportsField,
            airports: [...updatedAirports]
          },
          datesField: {
            ...state.fields.datesField,
            dates: [...updatedDates]
          }
        }
      };
    }
  )
);
